import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import StudentLayout from "@/components/StudentLayout";
import api from "@/utils/api";
import { BookOpen, Clock, CheckCircle, ArrowRight, Sparkles, Award, CreditCard, Download, AlertTriangle, FileText, ExternalLink, Image as ImageIcon, StickyNote, Megaphone } from "lucide-react";
import DOMPurify from "dompurify";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const paymentIconMap = {
  paid: { bg: "bg-[#5C3A2E]", icon: CheckCircle, iconClass: "w-4 h-4 text-white" },
  overdue: { bg: "bg-red-100", icon: AlertTriangle, iconClass: "w-4 h-4 text-red-500" },
  pending: { bg: "bg-[#C6A969]/20", icon: Clock, iconClass: "w-4 h-4 text-[#C6A969]" },
};

const paymentBadgeMap = {
  paid: { label: "Paid", className: "status-completed" },
  overdue: { label: "Overdue", className: "bg-red-100 text-red-800" },
  pending: { label: "Pending", className: "status-starting-soon" },
};

function StatusBadge({ status }) {
  const styles = {
    starting_soon: "status-starting-soon",
    in_progress: "status-in-progress",
    completed: "status-completed",
  };
  const labels = {
    starting_soon: "Starting Soon",
    in_progress: "In Progress",
    completed: "Completed",
  };
  return (
    <span data-testid="course-status-badge" className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${styles[status] || styles.starting_soon}`}>
      {labels[status] || "Starting Soon"}
    </span>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      api.get("/student/dashboard").then((r) => setData(r.data)).catch(() => {}),
      api.get("/student/payments").then((r) => setPayments(r.data)).catch(() => {}),
      api.get("/student/modules").then((r) => setAllModules(r.data)).catch(() => {}),
      api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {}),
      api.get("/student/progress").then((r) => setProgress(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const outstandingSummary = useMemo(() => {
    const pending = payments.filter((p) => p.status === "pending" || p.status === "overdue");
    const total = pending.reduce((sum, p) => sum + (p.amount || 0), 0);
    return { count: pending.length, total };
  }, [payments]);

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-[#5C3A2E]/50 font-body">Loading your studio...</div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="student-dashboard" className="space-y-10 animate-fade-in-up">
        {/* Greeting */}
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#5C3A2E] font-semibold tracking-tight">
            {getGreeting()}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#2B2B2B]/60 mt-2 text-base sm:text-lg font-body">
            Your creative journey continues. Here's where your craft comes to life.
          </p>
        </div>

        {/* Enrollment Pending */}
        {announcements.length > 0 && data?.enrollment_status === "accepted" && (
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} data-testid={`announcement-${a.id}`} className="bg-[#5C3A2E] rounded-xl px-5 py-4 flex items-start gap-3">
                <Megaphone className="w-5 h-5 text-[#C6A969] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{a.message}</p>
                  <p className="text-white/40 text-xs mt-1">{a.author_name} · {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress Bar */}
        {progress && progress.total_modules > 0 && data?.enrollment_status === "accepted" && (
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 font-medium">Your Progress</p>
              <span className="text-sm font-semibold text-[#5C3A2E]">{progress.completed_modules}/{progress.total_modules} modules</span>
            </div>
            <div className="w-full h-3 bg-[#EADBC8]/30 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#5C3A2E] to-[#C6A969] rounded-full transition-all duration-700" style={{ width: `${progress.percent}%` }} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-[#2B2B2B]/40">{progress.percent}% complete</span>
              {progress.current_module && <span className="text-xs text-[#C6A969]">Current: {progress.current_module.title}</span>}
            </div>
          </div>
        )}

        {data?.enrollment_status === "pending" && (
          <div className="bg-[#C6A969]/10 border border-[#C6A969]/30 rounded-xl p-8 sm:p-10 text-center">
            <Clock className="w-10 h-10 text-[#C6A969] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#5C3A2E] font-semibold mb-2">Enrollment Pending</h2>
            <p className="text-[#2B2B2B]/50 text-sm max-w-md mx-auto">
              Your enrollment is being reviewed. Your studio manager will approve your access shortly.
            </p>
          </div>
        )}

        {data?.enrollment_status === "rejected" && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 sm:p-10 text-center">
            <h2 className="font-heading text-2xl text-[#5C3A2E] font-semibold mb-2">Enrollment Not Approved</h2>
            <p className="text-[#2B2B2B]/50 text-sm max-w-md mx-auto">
              Please contact your studio manager for more information.
            </p>
          </div>
        )}

        {/* Course Card - only show when accepted */}
        {data?.enrollment_status !== "pending" && data?.enrollment_status !== "rejected" && data?.course ? (
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 overflow-hidden studio-card">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C6A969] font-medium mb-2">Your Course</p>
                  <h2 data-testid="course-title" className="font-heading text-2xl sm:text-3xl text-[#5C3A2E] font-semibold leading-tight">
                    {data.course.title}
                  </h2>
                </div>
                <StatusBadge status={data.course_status} />
              </div>
              {data.course.subtitle && (
                <p className="text-[#2B2B2B]/50 text-sm italic mb-3">{data.course.subtitle}</p>
              )}
              {data.group && (
                <div className="flex items-center gap-2 text-sm text-[#2B2B2B]/60 mt-3">
                  <Clock className="w-4 h-4" />
                  <span>{data.group.name} &middot; {data.group.schedule}</span>
                </div>
              )}
              {data.course.instructor && (
                <p className="text-sm text-[#2B2B2B]/50 mt-1">Instructor: {data.course.instructor}</p>
              )}
            </div>
          </div>
        ) : data?.enrollment_status !== "pending" && data?.enrollment_status !== "rejected" && (
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-8 sm:p-10 text-center">
            <Sparkles className="w-10 h-10 text-[#C6A969] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#5C3A2E] font-semibold mb-2">Your journey begins soon</h2>
            <p className="text-[#2B2B2B]/50 text-sm max-w-md mx-auto">
              You haven't been enrolled in a course yet. Your studio manager will assign you shortly.
            </p>
          </div>
        )}

        {/* Current Module - only for accepted students */}
        {data?.enrollment_status === "accepted" && data?.current_module && (
          <div className="animate-fade-in-up animate-stagger-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-4">Current Module</p>
            {data.completed_module_ids?.includes(data.current_module.id) ? (
              <div className="bg-[#5C3A2E] rounded-xl p-6 sm:p-8 text-center">
                <Award className="w-8 h-8 text-[#C6A969] mx-auto mb-3" />
                <h3 className="font-heading text-xl text-white font-semibold mb-1">Beautiful work</h3>
                <p className="text-white/70 text-sm mb-3">You've completed {data.current_module.title}</p>
                <Link to={`/module/${data.current_module.id}`} className="text-[#C6A969] text-sm hover:underline">View module details</Link>
              </div>
            ) : (
              <Link
                to={`/module/${data.current_module.id}`}
                data-testid="current-module-card"
                className="block bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 studio-card group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-[#C6A969]" />
                      <h3 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">
                        {data.current_module.title}
                      </h3>
                    </div>
                    {data.current_module.description && (
                      <p className="text-[#2B2B2B]/60 text-sm line-clamp-2 mt-1">{data.current_module.description}</p>
                    )}
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#5C3A2E]/30 group-hover:text-[#C6A969] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            )}
          </div>
        )}

        {/* Practical Sessions */}
        {data?.sessions && data.sessions.length > 0 && (
          <div className="animate-fade-in-up animate-stagger-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-4">Practical Sessions</p>
            <div className="grid gap-4">
              {data.sessions.map((session) => (
                <div
                  key={session.id}
                  data-testid={`session-card-${session.id}`}
                  className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6 studio-card space-y-4"
                >
                  <div>
                    <h4 className="font-heading text-lg text-[#5C3A2E] font-semibold">{session.title}</h4>
                    {session.description && (
                      <p className="text-[#2B2B2B]/50 text-sm mt-1">{session.description}</p>
                    )}
                  </div>

                  {/* Session Notes */}
                  {session.notes && (
                    <div className="border-t border-[#EADBC8]/30 pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <StickyNote className="w-4 h-4 text-[#5C3A2E]" />
                        <span className="text-xs uppercase tracking-wider text-[#2B2B2B]/40 font-medium">Notes</span>
                      </div>
                      <div className="text-[#2B2B2B]/70 text-sm leading-relaxed prose prose-sm max-w-none [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[#5C3A2E] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(session.notes) }} />
                    </div>
                  )}

                  {/* Session Resources */}
                  {session.materials && session.materials.length > 0 && (
                    <div className="border-t border-[#EADBC8]/30 pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-[#5C3A2E]" />
                        <span className="text-xs uppercase tracking-wider text-[#2B2B2B]/40 font-medium">Resources</span>
                      </div>
                      <div className="space-y-2">
                        {session.materials.map((mat, i) => (
                          <a
                            key={mat.id || i}
                            href={mat.type === "link" ? mat.url : `${API_BASE}/api/files/${mat.storage_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F7F3EF] hover:bg-[#EADBC8]/40 transition-colors"
                          >
                            {mat.type === "link" ? <ExternalLink className="w-3.5 h-3.5 text-[#C6A969]" /> : <Download className="w-3.5 h-3.5 text-[#5C3A2E]" />}
                            <span className="text-sm text-[#2B2B2B]">{mat.name || "Resource"}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Session Images */}
                  {session.images && session.images.length > 0 && (
                    <div className="border-t border-[#EADBC8]/30 pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ImageIcon className="w-4 h-4 text-[#5C3A2E]" />
                        <span className="text-xs uppercase tracking-wider text-[#2B2B2B]/40 font-medium">Gallery</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {session.images.map((img, i) => (
                          <div key={img.id || i} className="aspect-square rounded-xl overflow-hidden bg-[#F7F3EF]">
                            <img
                              src={`${API_BASE}/api/files/${img.storage_path}`}
                              alt={img.original_filename || "Session"}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Modules - completed + current (students can revisit) */}
        {data?.enrollment_status === "accepted" && allModules.length > 1 && (
          <div className="animate-fade-in-up animate-stagger-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-4">Your Modules</p>
            <div className="grid gap-3">
              {allModules.map((m, i) => {
                const isCompleted = m.is_completed;
                const isCurrent = m.is_current;
                const isAccessible = m.is_accessible;
                return (
                  <div key={m.id} className={`rounded-xl border p-4 sm:p-5 transition-all ${isAccessible ? "bg-white shadow-[0_8px_30px_rgb(92,58,46,0.08)] border-[#EADBC8]/30 studio-card" : "bg-[#F7F3EF] border-[#EADBC8]/20 opacity-50"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${isCompleted ? "bg-[#5C3A2E] text-white" : isCurrent ? "bg-[#C6A969] text-white" : "bg-[#EADBC8] text-[#5C3A2E]/50"}`}>
                          {isCompleted ? <CheckCircle className="w-4 h-4" /> : i + 1}
                        </span>
                        <div className="min-w-0">
                          <h4 className="text-sm font-medium text-[#2B2B2B] truncate">{m.title}</h4>
                          {isCurrent && <span className="text-xs text-[#C6A969]">Current</span>}
                          {isCompleted && <span className="text-xs text-[#5C3A2E]/50">Completed</span>}
                        </div>
                      </div>
                      {isAccessible && (
                        <Link to={`/module/${m.id}`} className="text-xs text-[#C6A969] hover:underline flex-shrink-0">View</Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Payments & Invoices */}
        {payments.length > 0 && (
          <div className="animate-fade-in-up animate-stagger-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-4">Payments & Invoices</p>
            <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 overflow-hidden">
              {outstandingSummary.total > 0 && (
                <div className="bg-[#EADBC8]/30 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#C6A969]" />
                    <span className="text-sm text-[#5C3A2E] font-medium">
                      {outstandingSummary.count} outstanding {outstandingSummary.count === 1 ? "payment" : "payments"}
                    </span>
                  </div>
                  <span className="text-lg font-semibold text-[#5C3A2E]">&euro;{outstandingSummary.total.toFixed(2)}</span>
                </div>
              )}
              <div className="divide-y divide-[#EADBC8]/30">
                {payments.map((p) => {
                  const status = p.status || "pending";
                  const iconCfg = paymentIconMap[status] || paymentIconMap.pending;
                  const badgeCfg = paymentBadgeMap[status] || paymentBadgeMap.pending;
                  const StatusIcon = iconCfg.icon;
                  return (
                    <div key={p.id} data-testid={`student-payment-${p.id}`} className="px-6 py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconCfg.bg}`}>
                          <StatusIcon className={iconCfg.iconClass} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#2B2B2B]">&euro;{Number(p.amount).toFixed(2)}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${badgeCfg.className}`}>
                              {badgeCfg.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#2B2B2B]/40 mt-0.5">
                            {p.due_date ? `Due ${p.due_date}` : ""}
                            {p.invoice_number ? ` \u00b7 Invoice #INV-${String(p.invoice_number).padStart(3, "0")}` : ""}
                          </p>
                        </div>
                      </div>
                      {p.invoice_path && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/student/payments/${p.id}/invoice`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-invoice-${p.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#5C3A2E] bg-[#EADBC8]/40 rounded-full hover:bg-[#EADBC8] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Invoice
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
